import { Pattern } from './Pattern';
import { PatternType, Difficulty, Bounds, Vector2, DotState } from '../types';

/**
 * Pinwheel - Four rotating arms of dots emanating from the arena center.
 * The arms rotate like a pinwheel / windmill. Player must find gaps and
 * slip through, or wait for arms to point away.
 */
export class Pinwheel extends Pattern {
  readonly type = PatternType.PINWHEEL;
  difficulty: Difficulty = Difficulty.MEDIUM;

  private readonly duration: number = 18000;
  private readonly armCount: number = 4;
  private readonly armLength: number = 12;
  private readonly dotSpacing: number = 32;
  private readonly gapEveryN: number = 4;
  private rotation: number = 0;
  private rotSpeed: number = 0.7;
  private center: Vector2 = { x: 0, y: 0 };
  private hasSpawned: boolean = false;

  constructor(difficulty: Difficulty = Difficulty.MEDIUM) {
    super();
    this.difficulty = difficulty;
    switch (difficulty) {
      case Difficulty.HARD:
        this.rotSpeed = 1.1;
        break;
      case Difficulty.MEDIUM:
        this.rotSpeed = 0.8;
        break;
      default:
        this.rotSpeed = 0.5;
    }
  }

  spawn(_center: Vector2, bounds: Bounds): void {
    this.start();
    this.center = { x: bounds.width / 2, y: bounds.height / 2 };
    this.rotation = Math.random() * Math.PI * 2;
    this.spawnArms();
    this.hasSpawned = true;
  }

  private spawnArms(): void {
    for (let arm = 0; arm < this.armCount; arm++) {
      const armAngle = (arm / this.armCount) * Math.PI * 2;
      for (let i = 1; i <= this.armLength; i++) {
        // Leave a gap every N dots so the arm is escapable
        if (i % this.gapEveryN === 0) continue;
        const dist = i * this.dotSpacing;
        const x = this.center.x + Math.cos(armAngle) * dist;
        const y = this.center.y + Math.sin(armAngle) * dist;
        const dot = this.spawnDot(x, y);
        dot.skipSpawnAnimation();
        dot.state = DotState.ACTIVE;
      }
    }
  }

  update(dt: number, playerPosition: Vector2, bounds: Bounds): void {
    this.rotation += this.rotSpeed * dt;

    // Rotate each dot around the center
    for (let i = this.dots.length - 1; i >= 0; i--) {
      const dot = this.dots[i];
      if (dot.isFrozen()) {
        dot.update(dt, bounds, playerPosition);
        continue;
      }

      const pos = dot.getPosition();
      const dx = pos.x - this.center.x;
      const dy = pos.y - this.center.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist > 0) {
        // Current angle and new angle
        const currentAngle = Math.atan2(dy, dx);
        const newAngle = currentAngle + this.rotSpeed * dt;
        dot.position.x = this.center.x + Math.cos(newAngle) * dist;
        dot.position.y = this.center.y + Math.sin(newAngle) * dist;
        // Velocity for visual purposes (and zombie-mode chasing after thaw)
        dot.velocity.x = -Math.sin(newAngle) * dist * this.rotSpeed;
        dot.velocity.y = Math.cos(newAngle) * dist * this.rotSpeed;
      }

      dot.update(dt, bounds, playerPosition);
    }

    // Clean up dots that have flown off (in case center wraps etc)
    for (let i = this.dots.length - 1; i >= 0; i--) {
      if (this.dots[i].isDead()) {
        this.dots.splice(i, 1);
      }
    }
  }

  isComplete(): boolean {
    return this.hasSpawned && this.elapsedMs > this.duration && this.dots.length === 0;
  }
}
